import { Component, computed, Signal, signal, WritableSignal } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { catchError, finalize, Observable, of, Subject, takeUntil } from 'rxjs';
import {
  ConfirmModalComponent,
  TConfirmModalParams,
} from 'src/app/project/components/confirm-modal/confirm-modal.component';
import { ModalService } from 'src/app/project/components/modal/modal.service';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { TPreferences, TRemindersPreferences } from '../../../preferences/preferences.model';
import { UserSettingsService } from '../user-settings.service';
import { EReminderOption } from './user-settings-reminders.consts';

type TReminderSettingsOption = {
  type: EReminderOption;
  header: string;
  subheader: string;
  enabled: boolean;
};

@Component({
  selector: 'pp-user-settings-reminders',
  templateUrl: './user-settings-reminders.component.html',
  styleUrl: './user-settings-reminders.component.scss',
  standalone: false,
})
export class UserSettingsRemindersComponent {
  options: TReminderSettingsOption[] = [
    {
      type: EReminderOption.ALERTS,
      header: this.translationPipe.transform('reminder_alerts_settings_header'),
      subheader: this.translationPipe.transform('reminder_alerts_settings_subheader'),
      enabled: true,
    },
    {
      type: EReminderOption.PUSH,
      header: this.translationPipe.transform('reminder_push_notifications_settings_header'),
      subheader: this.translationPipe.transform('reminder_push_notifications_settings_subheader'),
      enabled: true,
    },
    {
      type: EReminderOption.EMAIL,
      header: this.translationPipe.transform('reminder_email_settings_header'),
      subheader: this.translationPipe.transform('reminder_email_settings_subheader'),
      enabled: false,
    },
  ];

  preferences$: Observable<TPreferences>;
  visibleOptions: Signal<TReminderSettingsOption[]> = computed(() =>
    this.options.filter((option) => this.isOptionVisible(option.type)),
  );
  private readonly pushNotificationsEnabled: WritableSignal<boolean> = signal(false);
  private readonly destroy$ = new Subject<void>();
  private savingPreferences = false;

  constructor(
    private translationPipe: TranslationPipe,
    private store: Store<{
      preferences: TPreferences;
    }>,
    private userSettingsService: UserSettingsService,
    private modalService: ModalService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.setPushNotificationsEnabled();

    this.featureFlagService.flagsChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.setPushNotificationsEnabled());

    this.preferences$ = this.store.pipe(select(EStore.PREFERENCES));

    this.preferences$
      .pipe(takeUntil(this.destroy$))
      .subscribe((_preferences) => this.preferencesHandler(_preferences));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  private setPushNotificationsEnabled(): void {
    this.pushNotificationsEnabled.set(
      this.featureFlagService.getFlag(EFeatureFlag.PUSH_NOTIFICATION_PREFERENCES),
    );
  }

  private isOptionVisible(type: EReminderOption): boolean {
    if (type === EReminderOption.PUSH) {
      return this.pushNotificationsEnabled();
    }

    return true;
  }

  preferencesHandler(preferences: TPreferences): void {
    this.options[EReminderOption.ALERTS].enabled = preferences.reminderSettings?.alerts || false;
    this.options[EReminderOption.PUSH].enabled =
      preferences.reminderSettings?.pushNotifications || false;
    this.options[EReminderOption.EMAIL].enabled = preferences.reminderSettings?.emails || false;
  }

  toggleOption(option: TReminderSettingsOption, type: EReminderOption): void {
    if (this.savingPreferences) {
      return;
    }

    if (option.enabled) {
      this.confirmOptionChange(option);
    } else {
      this.showConfirmModal(option, type);
    }
  }

  private confirmOptionChange(option: TReminderSettingsOption): void {
    option.enabled = !option.enabled;

    this.saveReminderPreferences();
  }

  saveReminderPreferences(): void {
    if (!this.savingPreferences) {
      this.savingPreferences = true;

      const reminderSettings: TRemindersPreferences = {
        alerts: this.options[EReminderOption.ALERTS].enabled,
        pushNotifications: this.options[EReminderOption.PUSH].enabled,
        emails: this.options[EReminderOption.EMAIL].enabled,
      };

      this.userSettingsService
        .saveReminderPreferences(reminderSettings)
        .pipe(
          catchError(() => of([])),
          finalize(() => {
            this.savingPreferences = false;
          }),
        )
        .subscribe();
    }
  }

  private showConfirmModal(option: TReminderSettingsOption, type: EReminderOption): void {
    let message = '';
    let heading = '';

    switch (type) {
      case EReminderOption.ALERTS:
        message = this.translationPipe.transform('modal_enable_reminder_setting_description');
        heading = this.translationPipe.transform('modal_enable_reminder_setting_header');
        break;
      case EReminderOption.PUSH:
        message = this.translationPipe.transform('modal_enable_reminder_setting_push_description');
        heading = this.translationPipe.transform('modal_enable_reminder_setting_push_header');
        break;
      case EReminderOption.EMAIL:
        message = this.translationPipe.transform('modal_enable_reminder_setting_email_description');
        heading = this.translationPipe.transform('modal_enable_reminder_setting_email_header');
        break;
    }

    const modalData: TConfirmModalParams = {
      message: message,
      heading: heading,
      confirmText: this.translationPipe.transform('confirm'),
    };

    this.modalService.setData(modalData);

    this.modalService.showModal(ConfirmModalComponent, {
      blur: false,
      callback: () => {
        this.confirmOptionChange(option);
      },
    });
  }
}
